'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
    image?: string;
    name?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);

  return (
    <Link prefetchOnHover
      prefetch={false}
      href={productUrl}
      className={cn(
        'col-span-1 flex w-full flex-col items-center justify-center gap-4 text-center transition-all ease-in-out hover:opacity-80 sm:gap-5 lg:gap-6',
        className?.main,
      )}
    >
      <Image
        src={image?.url ?? ''}
        alt={resolveImageAlt(image, product?.name)}
        width={0}
        height={0}
        className={cn('aspect-square w-full shrink object-contain object-center', className?.image)}
        sizes="100vw"
      />
      <h5
        className={cn(
          'w-full text-center font-oswald text-lg font-medium uppercase text-black sm:text-xl lg:text-2xl',
          className?.name,
        )}
      >
        {product.name}
      </h5>
    </Link>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
